import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { MagnifyingGlass, Plus, User, Calendar, Phone } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

export default function Patients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (search = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params: search ? { q: search } : {} });
      setPatients(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = patients.filter((p) => {
      if (!p.created_at) return false;
      const d = new Date(p.created_at);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
    const withImplants = patients.filter((p) => (p.implants || []).length > 0).length;
    return { total: patients.length, thisMonth, withImplants };
  }, [patients]);

  return (
    <div className="space-y-8 fade-up" data-testid="patients-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Benvenuto, {user?.name}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1C2321] mt-2">Pazienti</h1>
          <p className="text-[#56635D] mt-2 max-w-xl">Gestisci pazienti, referti post-operatori e accesso al portale.</p>
        </div>
        <button
          onClick={() => navigate("/patients/new")}
          data-testid="new-patient-button"
          className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg transition-colors"
        >
          <Plus size={18} weight="bold" /> Nuovo paziente
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testId="stat-total" label="Pazienti totali" value={stats.total} />
        <StatCard testId="stat-month" label="Creati questo mese" value={stats.thisMonth} />
        <StatCard testId="stat-implants" label="Con impianti" value={stats.withImplants} />
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-400" weight="duotone" />
        <input
          data-testid="patient-search-input"
          placeholder="Cerca per nome, cognome, codice fiscale, telefono…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full pl-11 pr-4 py-3 rounded-lg border border-[#E6E8E6] bg-white focus:border-brand-700 transition-colors"
        />
      </div>

      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

      {/* Patient list */}
      <div className="space-y-3" data-testid="patients-list">
        {loading ? (
          <div className="text-center text-[#56635D] py-12">Caricamento…</div>
        ) : patients.length === 0 ? (
          <div className="elev-card p-10 text-center">
            <div className="w-12 h-12 rounded-lg bg-brand-50 mx-auto flex items-center justify-center mb-3">
              <User size={22} weight="duotone" className="text-brand-700" />
            </div>
            <div className="font-heading text-lg">Nessun paziente trovato</div>
            <p className="text-sm text-[#56635D] mt-1">Crea il primo paziente per iniziare.</p>
            <button onClick={() => navigate("/patients/new")} data-testid="empty-create-patient" className="mt-4 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 rounded-lg">
              <Plus size={16} weight="bold" /> Crea paziente
            </button>
          </div>
        ) : (
          patients.map((p) => (
            <Link
              to={`/patients/${p.id}`}
              key={p.id}
              data-testid={`patient-row-${p.id}`}
              className="elev-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:-translate-y-[1px]"
            >
              <div className="w-12 h-12 shrink-0 rounded-lg bg-brand-50 flex items-center justify-center font-heading font-semibold text-brand-700">
                {(p.first_name?.[0] || "").toUpperCase()}{(p.last_name?.[0] || "").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-medium text-[#1C2321] truncate">{p.first_name} {p.last_name}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#56635D] mt-1">
                  {p.fiscal_code && <span className="font-mono">{p.fiscal_code}</span>}
                  {p.phone && <span className="inline-flex items-center gap-1"><Phone size={12} weight="duotone" /> {p.phone}</span>}
                  {p.intervention_date && <span className="inline-flex items-center gap-1"><Calendar size={12} weight="duotone" /> {p.intervention_date}</span>}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(p.implants || []).length > 0 && (
                  <span className="text-xs font-medium bg-brand-100 text-brand-700 px-2.5 py-1 rounded-full">
                    {p.implants.length} {p.implants.length === 1 ? "impianto" : "impianti"}
                  </span>
                )}
                {p.intervention_type && (
                  <span className="text-xs font-medium bg-[#F0F0EE] text-[#56635D] px-2.5 py-1 rounded-full">
                    {p.intervention_type}
                  </span>
                )}
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="elev-card p-6" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.2em] text-brand-400">{label}</div>
      <div className="font-heading text-4xl font-medium text-[#1C2321] mt-2">{value}</div>
    </div>
  );
}
